import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  getSellerSingleOrderThunk,
  confirmSellerOrderThunk,
  rejectSellerOrderThunk,
} from "../../redux/slices/sellerOrderThunk";
import { clearSellerOrderMessages } from "../../redux/slices/sellerOrderSlice";
import SellerPaymentSection from "../../components/orders/SellerPaymentSection";
import SellerShipmentSection from "../../components/orders/SellerShipmentSection";
import styles from "../../styles/Seller/Sellerordermanage.module.css";
import CustomAlert from "../../components/alert/CustomAlert";

import {
  FiShoppingBag,
  FiMapPin,
  FiBox,
  FiCreditCard,
  FiTruck,
  FiSettings,
} from "react-icons/fi";

const Sellerordermanage = () => {
  const dispatch = useDispatch();
  const { orderId } = useParams();

  const [cancellationReason, setCancellationReason] = useState("");

  // Accordion open/close states
  const [isAddressOpen, setIsAddressOpen] = useState(false);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);    // New: Defaults open
  const [isShipmentOpen, setIsShipmentOpen] = useState(false);  // New: Defaults open

  const [alert, setAlert] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
  });
  const productsRef = useRef(null);
  const paymentRef = useRef(null);
  const shipmentRef = useRef(null);
  const actionsRef = useRef(null);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const {
    selectedOrder,
    singleOrderLoading,
    singleOrderError,
    confirmOrderLoading,
    rejectOrderLoading,
    confirmOrderSuccess,
    rejectOrderSuccess,
    confirmOrderError,
    rejectOrderError,
  } = useSelector((state) => state.sellerOrders);

  useEffect(() => {
    dispatch(getSellerSingleOrderThunk(orderId));
  }, [dispatch, orderId]);

  useEffect(() => {
    return () => {
      dispatch(clearSellerOrderMessages());
    };
  }, [dispatch]);

  useEffect(() => {
    if (confirmOrderSuccess) {
      setAlert({
        show: true,
        type: "success",
        title: "Order Confirmed",
        message: confirmOrderSuccess,
      });
    }
  }, [confirmOrderSuccess]);

  useEffect(() => {
    if (rejectOrderSuccess) {
      setAlert({
        show: true,
        type: "success",
        title: "Order Rejected",
        message: rejectOrderSuccess,
      });
    }
  }, [rejectOrderSuccess]);

  useEffect(() => {
    if (confirmOrderError) {
      setAlert({
        show: true,
        type: "error",
        title: "Confirmation Failed",
        message: confirmOrderError,
      });
    }
  }, [confirmOrderError]);

  useEffect(() => {
    if (rejectOrderError) {
      setAlert({
        show: true,
        type: "error",
        title: "Rejection Failed",
        message: rejectOrderError,
      });
    }
  }, [rejectOrderError]);

  const handleConfirmOrder = () => {
    dispatch(
      confirmSellerOrderThunk({
        orderId,
      }),
    );
  };

  const handleRejectOrder = () => {
    if (!cancellationReason.trim()) {
      setAlert({
        show: true,
        type: "warning",
        title: "Reason Required",
        message: "Please enter rejection reason",
      });

      return;
    }

    dispatch(
      rejectSellerOrderThunk({
        orderId,
        cancellationReason,
      }),
    );
  };

  if (singleOrderLoading) {
    return <div className={styles.loading}>Loading order...</div>;
  }

  if (singleOrderError) {
    return <div className={styles.error}>{singleOrderError}</div>;
  }

  if (!selectedOrder) {
    return <div className={styles.empty}>Order not found</div>;
  }

  return (
    <div className={styles.container}>
      {alert.show && (
        <CustomAlert
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={() =>
            setAlert((prev) => ({
              ...prev,
              show: false,
            }))
          }
        />
      )}
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerWrapper}>
          <div className={styles.headerTitle}>
            <div className={styles.headerIcon}>
              <FiShoppingBag />
            </div>

            <div>
              <h1 className={styles.heading}>Manage Order</h1>
              <p className={styles.subHeading}>
                View and manage your order details, shipments and payments.
              </p>
            </div>
          </div>

          <div className={styles.orderIdCard}>{selectedOrder.orderId}</div>
        </div>
      </div>

      {/* Quick Navigation Tabs */}
      <div className={styles.quickTabs}>
        <button onClick={() => scrollToSection(productsRef)}>
          <FiBox /> Products
        </button>

        <button onClick={() => scrollToSection(paymentRef)}>
          <FiCreditCard /> Payment
        </button>

        {selectedOrder.orderStatus !== "cancelled" && (
          <button onClick={() => scrollToSection(shipmentRef)}>
            <FiTruck /> Shipment
          </button>
        )}

        {selectedOrder.orderStatus === "pending" && (
          <button onClick={() => scrollToSection(actionsRef)}>
            <FiSettings /> Accept / Reject Order
          </button>
        )}
      </div>

      {/* Order Information Section */}
      <div className={styles.section}>
        <div 
          className={styles.dropdownHeader} 
        >
          <h2 className={styles.sectionTitle}>
            <FiShoppingBag className={styles.sectionIcon} />
            Order Information
          </h2>
        </div>

          <div className={styles.dropdownContent}>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span>Order ID</span>
                <strong>{selectedOrder.orderId}</strong>
              </div>

              <div className={styles.infoItem}>
                <span>Status</span>
                <strong className={styles.statusBadge}>
                  {selectedOrder.orderStatus}
                </strong>
              </div>

              <div className={styles.infoItem}>
                <span>Total Amount</span>
                <strong>₹ {selectedOrder.totalAmount}</strong>
              </div>

            </div>
          </div>
       
      </div>

      {/* Shipping Address Section */}
      <div className={styles.section}>
        <div 
          className={styles.dropdownHeader} 
          onClick={() => setIsAddressOpen(!isAddressOpen)}
        >
          <h2 className={styles.sectionTitle}>
            <FiMapPin className={styles.sectionIcon} />
            Shipping Address
          </h2>
          <span className={`${styles.toggleIcon} ${isAddressOpen ? styles.iconActive : ""}`}>
            {isAddressOpen ? "−" : "+"}
          </span>
        </div>

        {isAddressOpen && (
          <div className={styles.dropdownContent}>
            <p className={styles.address}>
              {selectedOrder.shippingAddress?.fullAddress}
            </p>
          </div>
        )}
      </div>

      {/* Products Section */}
      <div ref={productsRef} className={styles.section}>
        <div 
          className={styles.dropdownHeader} 
        >
          <h2 className={styles.sectionTitle}>
            <FiBox className={styles.sectionIcon} />
            Products
          </h2>
        </div>

          <div className={styles.dropdownContent}>
            <div className={styles.productsGrid}>
              {selectedOrder.orderItems?.map((item, index) => {
                const productImage =
                  item?.productImage?.data?.data && item?.productImage?.contentType
                    ? `data:${item.productImage.contentType};base64,${btoa(
                        new Uint8Array(item.productImage.data.data).reduce(
                          (data, byte) => data + String.fromCharCode(byte),
                          "",
                        ),
                      )}`
                    : null;

                return (
                  <div key={index} className={styles.productCard}>
                    <div className={styles.productImageWrapper}>
                      {productImage ? (
                        <img
                          src={productImage}
                          alt={item.productName}
                          className={styles.productImage}
                        />
                      ) : (
                        <div className={styles.noImage}>No Image</div>
                      )}
                    </div>

                    <div className={styles.productContent}>
                      <div className={styles.productTop}>
                        <div>
                          <h3 className={styles.productTitle}>
                            {item.productName}
                          </h3>
                          <p className={styles.productCategory}>{item.category}</p>
                        </div>

                        <div className={styles.priceBadge}>
                          ₹ {item.pricePerMT} /MT
                        </div>
                      </div>

                      <div className={styles.productInfoGrid}>
                        <div className={styles.infoBox}>
                          <span>Quantity</span>
                          <strong>{item.requiredQuantity} MT</strong>
                        </div>

                        <div className={styles.infoBox}>
                          <span>Subtotal</span>
                          <strong>₹ {item.subtotal}</strong>
                        </div>

                        <div className={styles.infoBox}>
                          <span>Loading Location</span>
                          <strong>{item.loadingLocation || "N/A"}</strong>
                        </div>

                        <div className={styles.infoBox}>
                          <span>HSN Code</span>
                          <strong>{item.hsnCode || "N/A"}</strong>
                        </div>
                      </div>

                      {item.subProducts?.length > 0 && (
                        <div className={styles.subProductsSection}>
                          <span className={styles.subProductsLabel}>
                            Sub Products
                          </span>
                          <div className={styles.subProductsWrap}>
                            {item.subProducts.map((sub, subIndex) => (
                              <span key={subIndex} className={styles.subProductTag}>
                                {sub}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        
      </div>

      {/* Payment Section - Handled via clean Section Structure wrappers */}
      <div ref={paymentRef} className={styles.section}>
        <div 
          className={styles.dropdownHeader} 
          onClick={() => setIsPaymentOpen(!isPaymentOpen)}
        >
          <h2 className={styles.sectionTitle}>
            <FiCreditCard className={styles.sectionIcon} />
            Payment Details
          </h2>
          <span className={`${styles.toggleIcon} ${isPaymentOpen ? styles.iconActive : ""}`}>
            {isPaymentOpen ? "−" : "+"}
          </span>
        </div>
        {isPaymentOpen && (
          <div className={styles.dropdownContent}>
            <SellerPaymentSection selectedOrder={selectedOrder} />
          </div>
        )}
      </div>

      {/* Shipment Section - Handled via clean Section Structure wrappers */}
      {selectedOrder.orderStatus !== "cancelled" && (
        <div ref={shipmentRef} className={styles.section}>
          <div 
            className={styles.dropdownHeader} 
            onClick={() => setIsShipmentOpen(!isShipmentOpen)}
          >
            <h2 className={styles.sectionTitle}>
              <FiTruck className={styles.sectionIcon} />
              Shipment Details
            </h2>
            <span className={`${styles.toggleIcon} ${isShipmentOpen ? styles.iconActive : ""}`}>
              {isShipmentOpen ? "−" : "+"}
            </span>
          </div>
          {isShipmentOpen && (
            <div className={styles.dropdownContent}>
              <SellerShipmentSection selectedOrder={selectedOrder} />
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      {selectedOrder.orderStatus === "pending" && (
        <div ref={actionsRef} className={styles.actionsSection}>
          <h2 className={styles.actionsHeading}>
            <span className={styles.actionsIcon}>⚙️</span>
            Actions
          </h2>

          <div className={styles.actionsGrid}>
            {/* Confirm Order */}
            <div className={styles.confirmCard}>
              <div className={styles.cardHeader}>
                <span className={styles.greenIcon}>✔</span>
                <h3>Confirm Order</h3>
              </div>

              <p>
                Please confirm the order after reviewing all order details.
                Marketplace transport workflow will automatically start after
                confirmation.
              </p>

              <button
                className={styles.confirmButton}
                onClick={handleConfirmOrder}
                disabled={confirmOrderLoading}
              >
                {confirmOrderLoading ? "Confirming..." : "Confirm Order"}
              </button>
            </div>

            {/* Reject Order */}
            <div className={styles.cancelCard}>
              <div className={styles.cardHeader}>
                <span className={styles.redIcon}>✕</span>
                <h3>Reject Order</h3>
              </div>

              <p>
                If you want to reject this order, please provide a valid reason.
                The buyer will be notified after rejection.
              </p>

              <label className={styles.inputLabel}>Rejection Reason *</label>

              <textarea
                className={styles.textarea}
                placeholder="Enter rejection reason..."
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                required
              />

              <button
                className={styles.rejectButton}
                onClick={handleRejectOrder}
                disabled={rejectOrderLoading}
              >
                {rejectOrderLoading ? "Rejecting..." : "Reject Order"}
              </button>
            </div>
          </div>

          <div className={styles.warningNote}>
            ℹ Please review all order details carefully before confirming or
            rejecting this order.
          </div>
        </div>
      )}
    </div>
  );
};

export default Sellerordermanage;